import React from 'react'
import { connect } from 'react-redux'
import { Card, CardBody } from 'reactstrap'
import { sendMessage, sessionCleared, sessionRestored } from '../actions/agentQueryActions'
import ChatBubble from '../components/chat/ChatBubble'
import ChatInput from '../components/chat/ChatInput'
import { AppStore } from '../reducers/states'
import { AgentQueryStore } from '../reducers/agentQueryReducer'
import { ChatMessage } from '../actions/agentQueryActions'

interface Props extends AgentQueryStore {
  dispatch: (action: any) => void
}

const STORAGE_SESSION_ID_KEY = 'orderAssistant_sessionId'
const STORAGE_MESSAGES_KEY = 'orderAssistant_messages'
const STORAGE_TIMESTAMP_KEY = 'orderAssistant_timestamp'
const SESSION_DURATION_MS = 30 * 60 * 1000 // 30 minutes

class OrderAssistantView extends React.Component<Props> {
  private messagesEndRef: React.RefObject<HTMLDivElement>

  constructor(props: Props) {
    super(props)
    this.messagesEndRef = React.createRef()
  }

  componentDidMount() {
    this.restoreSessionFromLocalStorage()
  }

  componentDidUpdate() {
    this.scrollToBottom()
  }

  private restoreSessionFromLocalStorage() {
    try {
      const storedSessionId = localStorage.getItem(STORAGE_SESSION_ID_KEY)
      const storedMessagesJson = localStorage.getItem(STORAGE_MESSAGES_KEY)
      const storedTimestamp = localStorage.getItem(STORAGE_TIMESTAMP_KEY)

      if (storedSessionId && storedMessagesJson && storedTimestamp) {
        const timestamp = parseInt(storedTimestamp, 10)
        const now = Date.now()

        // Check if session is still valid (within 30 minutes)
        if (now - timestamp < SESSION_DURATION_MS) {
          try {
            const messages: ChatMessage[] = JSON.parse(storedMessagesJson)
            this.props.dispatch(sessionRestored({ sessionId: storedSessionId, messages }))
          } catch (e) {
            // JSON parse error, discard silently
          }
        }
      }
    } catch (e) {
      // localStorage error, discard silently
    }
  }

  private scrollToBottom() {
    if (this.messagesEndRef.current) {
      this.messagesEndRef.current.scrollIntoView({ behavior: 'auto' })
    }
  }

  private handleSendMessage = (text: string) => {
    const trimmed = text.trim()
    if (trimmed.length > 0) {
      this.props.dispatch(
        sendMessage({
          prompt: trimmed,
          sessionId: this.props.sessionId,
        })
      )
    }
  }

  private handleNewConversation = () => {
    // Clear localStorage
    localStorage.removeItem(STORAGE_SESSION_ID_KEY)
    localStorage.removeItem(STORAGE_MESSAGES_KEY)
    localStorage.removeItem(STORAGE_TIMESTAMP_KEY)
    // Dispatch session cleared action
    this.props.dispatch(sessionCleared())
  }

  render() {
    return (
      <Card style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardBody
          style={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            padding: '16px',
          }}
        >
          {/* Message list container */}
          <div
            style={{
              flex: 1,
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              marginBottom: '16px',
              border: '1px solid #e0e0e0',
              borderRadius: '4px',
              padding: '12px',
              backgroundColor: '#fafafa',
            }}
          >
            {this.props.messages.length === 0 ? (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  color: '#999',
                  fontSize: '14px',
                  textAlign: 'center',
                }}
              >
                Ask me anything about an order or customer.
              </div>
            ) : (
              <>
                {this.props.messages.map((message) => (
                  <ChatBubble key={message.id} message={message} />
                ))}
                <div ref={this.messagesEndRef} />
              </>
            )}
          </div>

          {/* Input area */}
          <ChatInput
            isLoading={this.props.isLoading}
            messageCount={this.props.messages.length}
            onSend={this.handleSendMessage}
            onNewConversation={this.handleNewConversation}
          />
        </CardBody>
      </Card>
    )
  }
}

function mapStateToProps(state: AppStore) {
  return {
    ...state.agentQuery,
  } as Props
}

export default connect(mapStateToProps)(OrderAssistantView)
